import { ClockIcon, BuildingIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { IconButton, Spinner, Chip } from '@material-tailwind/react';
import { PencilIcon, SaveIcon, XIcon } from 'lucide-react';
import { useUpdateUserMeetingRoomQuota } from '../../../custom-hooks/use-users';
import { GetMeetingRooms } from '../../../services/backend';

const MeetingQuota = ({ customer }) => {
    const [isEditingMeetingQuota, setIsEditingMeetingQuota] = useState(false);
    const [meetingRooms, setMeetingRooms] = useState([]);
    const [loadingRooms, setLoadingRooms] = useState(false);
    const [isRoomListOpen, setIsRoomListOpen] = useState(false);

    useEffect(() => {
        if (isEditingMeetingQuota) {
            setIsRoomListOpen(true);
        } else {
            setIsRoomListOpen(false);
        }
    }, [isEditingMeetingQuota]);

    const [meetingRoomQuota, setMeetingRoomQuota] = useState({
        monthlyHours: (customer?.meetingRoomQuota?.monthlyHours) / 60 || 0,
        meetingRoomHoursPerDay: (customer?.meetingRoomQuota?.meetingRoomHoursPerDay) / 60 || 0,
    });

    // Per-room sub-limits: [{ room, monthlyHours, dailyHours }]
    const [roomQuotas, setRoomQuotas] = useState(
        (customer?.roomQuotas || []).map(rq => ({
            room: rq.room?._id || rq.room,
            monthlyHours: (rq.monthlyMinutes || 0) / 60,
            dailyHours: (rq.dailyMinutes || 0) / 60,
            usedMinutes: rq.usedMinutes || 0,
        }))
    );

    const updateUserMeetingRoomQuotaMutation = useUpdateUserMeetingRoomQuota();

    useEffect(() => {
        const fetchRooms = async () => {
            try {
                setLoadingRooms(true);
                const res = await GetMeetingRooms();
                setMeetingRooms(res.data.meetingRooms || []);
            } catch (e) {
                console.error('Failed to fetch meeting rooms:', e);
            } finally {
                setLoadingRooms(false);
            }
        };
        fetchRooms();
    }, []);

    const getRoomQuota = (roomId) => {
        return roomQuotas.find(rq => rq.room?.toString() === roomId?.toString()) || { room: roomId, monthlyHours: 0, dailyHours: 0, usedMinutes: 0 };
    };

    const updateRoomQuota = (roomId, field, value) => {
        const parsed = parseFloat(value) || 0;
        setRoomQuotas(prev => {
            let nextQuotas = [];
            const existing = prev.find(rq => rq.room?.toString() === roomId?.toString());
            if (existing) {
                nextQuotas = prev.map(rq => rq.room?.toString() === roomId?.toString() ? { ...rq, [field]: parsed } : rq);
            } else {
                nextQuotas = [...prev, { room: roomId, monthlyHours: 0, dailyHours: 0, usedMinutes: 0, [field]: parsed }];
            }

            if (field === 'monthlyHours') {
                const totalMonthly = nextQuotas.reduce((acc, curr) => acc + (parseFloat(curr.monthlyHours) || 0), 0);
                setMeetingRoomQuota(prevQuota => ({
                    ...prevQuota,
                    monthlyHours: totalMonthly
                }));
            }

            return nextQuotas;
        });
    };

    const handleSaveMeetingQuota = async () => {
        try {
            // Only include rooms that have at least one non-zero value
            const filteredRoomQuotas = roomQuotas.filter(rq => rq.monthlyHours > 0 || rq.dailyHours > 0).map(rq => ({
                room: rq.room,
                monthlyMinutes: Math.round(rq.monthlyHours * 60) || 0,
                dailyMinutes: Math.round(rq.dailyHours * 60) || 0
            }));
            
            await updateUserMeetingRoomQuotaMutation.mutateAsync({
                userId: customer._id,
                data: {
                    monthlyHours: parseInt(meetingRoomQuota.monthlyHours) || 0,
                    meetingRoomHoursPerDay: parseInt(meetingRoomQuota.meetingRoomHoursPerDay) || 0,
                    roomQuotas: filteredRoomQuotas,
                }
            });
            setIsEditingMeetingQuota(false);
        } catch (error) {
            console.error('Error updating meeting room quota:', error);
        }
    };

    const handleCancelEdit = () => {
        setMeetingRoomQuota({
            monthlyHours: (customer?.meetingRoomQuota?.monthlyHours) / 60 || 0,
            meetingRoomHoursPerDay: (customer?.meetingRoomQuota?.meetingRoomHoursPerDay) / 60 || 0,
        });
        setRoomQuotas((customer?.roomQuotas || []).map(rq => ({
            room: rq.room?._id || rq.room,
            monthlyHours: (rq.monthlyMinutes || 0) / 60,
            dailyHours: (rq.dailyMinutes || 0) / 60,
            usedMinutes: rq.usedMinutes || 0,
        })));
        setIsEditingMeetingQuota(false);
    };

    const isSaving = updateUserMeetingRoomQuotaMutation.isLoading;

    return (
        <div className="mt-6 pt-6 border-t">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                    <ClockIcon size={16} />
                    Meeting Room Quota
                </h2>
                {!isEditingMeetingQuota ? (
                    <IconButton variant="text" size="sm" onClick={() => setIsEditingMeetingQuota(true)} className="rounded-full">
                        <PencilIcon size={16} />
                    </IconButton>
                ) : (
                    <div className="flex gap-2">
                        <IconButton variant="gradient" color="blue" size="sm" onClick={handleSaveMeetingQuota} className="rounded-full" disabled={isSaving}>
                            {isSaving ? <Spinner className="h-4 w-4" /> : <SaveIcon size={16} />}
                        </IconButton>
                        <IconButton variant="outlined" size="sm" onClick={handleCancelEdit} className="rounded-full" disabled={isSaving}>
                            <XIcon size={16} />
                        </IconButton>
                    </div>
                )}
            </div>

            {/* Global Quota Display / Edit */}
            {!isEditingMeetingQuota ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-200 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Organization Pool (Monthly)</p>
                        <p className="text-2xl font-bold text-gray-800">{meetingRoomQuota?.monthlyHours || 0} hrs</p>
                        <p className="text-xs text-gray-500 mt-1">
                            Used: {((customer?.meetingRoomQuota?.usedHours || 0) / 60).toFixed(1)} hrs
                        </p>
                    </div>
                    <div className="bg-gray-200 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Daily Limit (Org-wide)</p>
                        <p className="text-2xl font-bold text-gray-800">{meetingRoomQuota?.meetingRoomHoursPerDay || 0} hrs/day</p>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                        <p className="text-sm text-gray-600 mb-1">Organization Pool Monthly Hours</p>
                        <input
                            type="number"
                            className="w-full border border-gray-300 rounded-lg bg-gray-100 text-gray-500 px-3 py-2 text-sm outline-none transition cursor-not-allowed"
                            value={meetingRoomQuota.monthlyHours}
                            max={30}
                            min={0}
                            step={1}
                            readOnly
                            disabled={true}
                        />
                    </div>
                    <div className="flex flex-col">
                        <p className="text-sm text-gray-600 mb-1">Daily Limit ({meetingRoomQuota.meetingRoomHoursPerDay} hrs/day)</p>
                        <input
                            type="range"
                            className="w-full mt-2"
                            value={meetingRoomQuota.meetingRoomHoursPerDay}
                            max={10}
                            min={0}
                            step={1}
                            onChange={(e) => setMeetingRoomQuota({ ...meetingRoomQuota, meetingRoomHoursPerDay: e.target.value })}
                            disabled={isSaving}
                        />
                    </div>
                </div>
            )}

            {/* Per-Room Sub-Limits */}
            <div className="mt-6">
                <div 
                    className="flex items-center gap-2 mb-3 cursor-pointer select-none hover:bg-gray-50 p-2 -ml-2 rounded-lg transition-colors w-max"
                    onClick={() => setIsRoomListOpen(!isRoomListOpen)}
                >
                    <div className="flex items-center justify-center w-5 h-5 rounded-md bg-gray-100 text-gray-500 flex-shrink-0">
                        {isRoomListOpen ? <ChevronUpIcon size={14} /> : <ChevronDownIcon size={14} />}
                    </div>
                    <BuildingIcon size={14} className="text-gray-500" />
                    <p className="text-sm font-semibold text-gray-600">Per-Room Sub-Limits</p>
                    <span className="text-xs text-gray-400 hidden sm:inline">(0 = no room-specific cap)</span>
                </div>

                <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isRoomListOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                    <div className="overflow-hidden">
                        {loadingRooms ? (
                            <div className="flex justify-center py-4"><Spinner className="h-5 w-5" /></div>
                        ) : meetingRooms.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No meeting rooms found.</p>
                        ) : (
                            <div className="space-y-3 pb-2">
                        {meetingRooms.map(room => {
                            const rq = getRoomQuota(room._id);
                            return (
                                <div key={room._id} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: room.idColor || '#ccc' }} />
                                        <p className="text-sm font-medium text-gray-700 truncate">{room.code} — {room.number}</p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0">
                                        {rq.usedMinutes > 0 && (
                                            <p className="text-xs text-gray-400 mr-2">Used: {(rq.usedMinutes / 60).toFixed(1)} hrs</p>
                                        )}
                                        {!isEditingMeetingQuota ? (
                                            <div className="flex gap-2 flex-shrink-0">
                                                <Chip
                                                    size="sm"
                                                    value={rq.monthlyHours > 0 ? `${rq.monthlyHours} hr/mo` : 'No monthly cap'}
                                                    className={rq.monthlyHours > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}
                                                />
                                                <Chip
                                                    size="sm"
                                                    value={rq.dailyHours > 0 ? `${rq.dailyHours} hr/day` : 'No daily cap'}
                                                    className={rq.dailyHours > 0 ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-500'}
                                                />
                                            </div>
                                        ) : (
                                            <div className="flex gap-2 flex-shrink-0">
                                                <div className="flex flex-col items-start sm:items-center gap-0.5">
                                                    <label className="text-xs text-gray-500 font-medium">Monthly (hrs)</label>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step={0.5}
                                                        className="w-16 sm:w-20 text-center border border-gray-300 rounded px-1 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                        value={rq.monthlyHours}
                                                        onChange={e => updateRoomQuota(room._id, 'monthlyHours', e.target.value)}
                                                        disabled={isSaving}
                                                        placeholder="0"
                                                    />
                                                </div>
                                                <div className="flex flex-col items-start sm:items-center gap-0.5">
                                                    <label className="text-xs text-gray-500 font-medium">Daily (hrs)</label>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step={0.5}
                                                        className="w-16 sm:w-20 text-center border border-gray-300 rounded px-1 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-400"
                                                        value={rq.dailyHours}
                                                        onChange={e => updateRoomQuota(room._id, 'dailyHours', e.target.value)}
                                                        disabled={isSaving}
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                            </div>
                        )}
                        {isEditingMeetingQuota && (
                            <p className="text-xs text-gray-400 mt-2">
                                Enter limits in <strong>hours</strong>. E.g. 1.5 = 1 hour 30 mins. Rooms with 0 in both fields use the global pool only.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MeetingQuota;
